import {
  PROVIDER_CATALOG,
  PROVIDER_KINDS,
  providerIssues,
  type DraftProvider,
  type Hosted,
  type ModelListResult,
  type ProviderCheckResult,
  type ProviderKind,
  type ProviderView,
  type StructuredOutput,
} from '@cyberscore/contracts';
import { CheckCircle2, CircleAlert, Eye, EyeOff, ListRestart, Loader2, Plug } from 'lucide-react';
import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { ApiError } from '../../lib/api';
import { Drawer } from '../../ui/Overlay';
import { useToast } from '../../ui/Toasts';
import { checkDraft, listModels, useCreateProvider, useUpdateProvider } from './api';

type Thinking = 'default' | 'off' | 'on';

interface FormState {
  kind: ProviderKind;
  name: string;
  customUrl: boolean;
  base_url: string;
  model: string;
  api_key: string;
  removeKey: boolean;
  hosted: Hosted;
  thinking: Thinking;
  structured_output: StructuredOutput;
  price_in: string;
  price_out: string;
  enabled: boolean;
  is_default: boolean;
}

const KIND_HINT: Record<ProviderKind, string> = {
  together: 'MiniMax, Llama, Qwen and more',
  openai: 'GPT models',
  anthropic: 'Claude models',
  google: 'Gemini models',
  openai_compatible: 'Your own server: vLLM, Ollama, LiteLLM',
};

const toThinking = (r: boolean | null): Thinking => (r === null ? 'default' : r ? 'on' : 'off');
const fromThinking = (t: Thinking) => (t === 'default' ? null : t === 'on');

function initial(p: ProviderView | undefined, first: boolean): FormState {
  if (p) {
    return {
      kind: p.kind,
      name: p.name,
      customUrl: p.base_url !== null && PROVIDER_CATALOG[p.kind].defaultBaseUrl !== null,
      base_url: p.base_url ?? '',
      model: p.model,
      api_key: '',
      removeKey: false,
      hosted: p.hosted,
      thinking: toThinking(p.reasoning),
      structured_output: p.structured_output,
      price_in: p.price_input_per_mtok ? String(p.price_input_per_mtok) : '',
      price_out: p.price_output_per_mtok ? String(p.price_output_per_mtok) : '',
      enabled: p.enabled,
      is_default: p.is_default,
    };
  }
  return { ...forKind('together'), name: 'Together AI', is_default: first };
}

/** Settings that follow from the kind when it changes on a new provider. */
function forKind(kind: ProviderKind): Omit<FormState, 'name' | 'is_default'> {
  const info = PROVIDER_CATALOG[kind];
  return {
    kind,
    customUrl: false,
    base_url: '',
    model: '',
    api_key: '',
    removeKey: false,
    hosted: info.defaultHosted,
    // MiniMax M3 on Together thinks by default; chat turns need it off for speed.
    thinking: kind === 'together' ? 'off' : 'default',
    structured_output: 'function_calling',
    price_in: '',
    price_out: '',
    enabled: true,
  };
}

export function ProviderForm({
  provider,
  isFirst,
  onClose,
}: {
  provider?: ProviderView;
  isFirst: boolean;
  onClose: () => void;
}) {
  const editing = !!provider;
  const [f, setF] = useState<FormState>(() => initial(provider, isFirst));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showKey, setShowKey] = useState(false);
  const [check, setCheck] = useState<ProviderCheckResult | { error: string } | null>(null);
  const [checking, setChecking] = useState(false);
  const [models, setModels] = useState<ModelListResult | { error: string } | null>(null);
  const [loadingModels, setLoadingModels] = useState(false);
  const [banner, setBanner] = useState<string | null>(null);
  const create = useCreateProvider();
  const update = useUpdateProvider();
  const toast = useToast();
  const ids = { models: useId(), key: useId() };
  const resultRef = useRef<HTMLDivElement>(null);
  // The result sits at the end of a long form: bring it into view when it arrives.
  useEffect(() => {
    if (check || checking) resultRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [check, checking]);

  const info = PROVIDER_CATALOG[f.kind];
  const baseUrl = info.defaultBaseUrl === null || f.customUrl ? f.base_url.trim() || null : null;
  const keyTyped = f.api_key.trim();
  const hasKey = keyTyped ? true : f.removeKey ? false : !!provider?.has_key;
  // A saved key is only sent to the URL it was entered for (the server enforces this too).
  const keyNeedsRetyping =
    editing && !!provider?.has_key && !keyTyped && !f.removeKey && baseUrl !== provider.base_url;

  const set = <K extends keyof FormState>(k: K, v: FormState[K]) => {
    setF((s) => ({ ...s, [k]: v }));
    setErrors((e) => {
      const rest = { ...e };
      delete rest[k === 'customUrl' ? 'base_url' : k];
      return rest;
    });
    if (['base_url', 'customUrl', 'model', 'api_key', 'thinking', 'structured_output'].includes(k))
      setCheck(null);
  };

  const pickKind = (kind: ProviderKind) => {
    if (editing || kind === f.kind) return;
    setF((s) => ({
      ...forKind(kind),
      // Keep a name the admin typed; replace one we suggested.
      name: s.name === PROVIDER_CATALOG[s.kind].label || !s.name ? PROVIDER_CATALOG[kind].label : s.name,
      is_default: s.is_default,
    }));
    setErrors({});
    setCheck(null);
    setModels(null);
  };

  const draft = (): DraftProvider => ({
    provider_id: editing && !keyTyped ? provider!.id : undefined,
    kind: f.kind,
    base_url: baseUrl,
    model: f.model.trim(),
    api_key: keyTyped || undefined,
    reasoning: info.thinkingSwitch ? fromThinking(f.thinking) : null,
    structured_output: f.structured_output,
  });

  const validate = (forSave: boolean): Record<string, string> => {
    const e: Record<string, string> = {};
    if (forSave && !f.name.trim()) e.name = 'Give the provider a name';
    if (forSave && !f.model.trim()) e.model = 'Choose a model';
    if (baseUrl && !/^https?:\/\/[^\s/]+/.test(baseUrl)) e.base_url = 'Enter a full URL, e.g. https://…';
    if (keyTyped && keyTyped.length < 8) e.api_key = 'That key looks too short';
    if (keyNeedsRetyping) e.api_key = 'Enter the API key again to use it with the new URL';
    for (const i of providerIssues({
      kind: f.kind,
      base_url: baseUrl,
      has_key: hasKey,
      enabled: f.enabled,
      is_default: f.is_default,
    })) {
      if (forSave || i.path !== 'enabled') e[i.path] ??= i.message;
    }
    for (const [k, v] of [
      ['price_in', f.price_in],
      ['price_out', f.price_out],
    ] as const) {
      if (v && !(Number(v) >= 0 && Number(v) <= 1000)) e[k] = 'Enter a price between 0 and 1000';
    }
    return e;
  };

  const fail = (e: unknown) => {
    if (e instanceof ApiError) {
      if (e.status === 409 && e.code === 'revision_conflict') {
        setBanner(
          'Someone else saved this provider while you were editing. Close and reopen it to see their changes.',
        );
      } else if (e.issues.length) {
        setErrors(Object.fromEntries(e.issues.map((i) => [mapPath(i.path), i.message])));
        setBanner(null);
      } else {
        setBanner(e.message);
      }
    } else {
      setBanner('Something went wrong. Try again.');
    }
  };

  const runCheck = async () => {
    const e = validate(false);
    if (!f.model.trim()) e.model = 'Choose a model to test';
    setErrors(e);
    if (Object.keys(e).length) return;
    setChecking(true);
    setCheck(null);
    try {
      setCheck(await checkDraft(draft()));
    } catch (err) {
      if (err instanceof ApiError && err.issues.length) fail(err);
      setCheck({ error: err instanceof ApiError ? err.message : 'The check could not run' });
    } finally {
      setChecking(false);
    }
  };

  const loadModels = async () => {
    const e = validate(false);
    delete e.model;
    setErrors(e);
    if (Object.keys(e).length) return;
    setLoadingModels(true);
    try {
      setModels(await listModels(draft()));
    } catch (err) {
      setModels({ error: err instanceof ApiError ? err.message : 'Could not load models' });
    } finally {
      setLoadingModels(false);
    }
  };

  const save = async () => {
    const e = validate(true);
    setErrors(e);
    if (Object.keys(e).length) return;
    setBanner(null);
    const settings = {
      name: f.name.trim(),
      base_url: baseUrl,
      model: f.model.trim(),
      hosted: f.hosted,
      reasoning: info.thinkingSwitch ? fromThinking(f.thinking) : null,
      structured_output: f.structured_output,
      price_input_per_mtok: Number(f.price_in || 0),
      price_output_per_mtok: Number(f.price_out || 0),
      enabled: f.enabled,
      is_default: f.is_default,
    };
    try {
      if (!provider) {
        await create.mutateAsync({ ...settings, kind: f.kind, api_key: keyTyped || undefined });
        toast(`${settings.name} added. Run Test to check the connection.`);
      } else {
        const changed = Object.fromEntries(
          Object.entries(settings).filter(
            ([k, v]) => v !== (current(provider) as Record<string, unknown>)[k],
          ),
        );
        await update.mutateAsync({
          id: provider.id,
          revision: provider.revision,
          ...changed,
          ...(keyTyped ? { api_key: keyTyped } : f.removeKey ? { api_key: null } : {}),
        });
        toast(`${settings.name} saved.`);
      }
      onClose();
    } catch (err) {
      fail(err);
    }
  };

  const saving = create.isPending || update.isPending;
  const modelOptions = models && 'models' in models ? models.models : [];

  return (
    <Drawer
      title={editing ? `Edit ${provider!.name}` : 'Add provider'}
      subtitle={
        editing
          ? `${info.label} · revision ${provider!.revision}`
          : 'Connect a model API. The assessment chat uses the default provider.'
      }
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={runCheck} disabled={checking || saving}>
            {checking ? <Loader2 size={15} className="spin" /> : <Plug size={15} />}
            Test connection
          </button>
          <span className="grow" />
          <button type="button" className="btn" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="btn btn-primary" onClick={save} disabled={saving}>
            {saving && <Loader2 size={15} className="spin" />}
            {editing ? 'Save changes' : 'Add provider'}
          </button>
        </>
      }
    >
      {banner && (
        <div className="result bad" role="alert">
          <CircleAlert size={16} />
          <span>{banner}</span>
        </div>
      )}

      <div className="field">
        <span className="field-label" id="kind-label">
          Provider type
        </span>
        <div className="kinds" role="radiogroup" aria-labelledby="kind-label">
          {PROVIDER_KINDS.map((k) => (
            <button
              key={k}
              type="button"
              role="radio"
              aria-label={PROVIDER_CATALOG[k].label}
              className="kind"
              aria-checked={f.kind === k}
              disabled={editing && f.kind !== k}
              onClick={() => pickKind(k)}
            >
              <span>{PROVIDER_CATALOG[k].label}</span>
              <small>{KIND_HINT[k]}</small>
            </button>
          ))}
        </div>
        {editing && (
          <span className="hint">The type can’t change. Add a new provider for another vendor.</span>
        )}
      </div>

      <Field label="Name" error={errors.name}>
        {(id) => (
          <input
            id={id}
            className={`input${errors.name ? ' invalid' : ''}`}
            value={f.name}
            maxLength={80}
            onChange={(e) => set('name', e.target.value)}
          />
        )}
      </Field>

      {info.defaultBaseUrl === null ? (
        <Field
          label="Base URL"
          error={errors.base_url}
          hint="The OpenAI-compatible endpoint, usually ending in /v1. Plain http is allowed only here, for servers on your own network."
        >
          {(id) => (
            <input
              id={id}
              className={`input mono${errors.base_url ? ' invalid' : ''}`}
              placeholder="http://10.0.0.5:8000/v1"
              value={f.base_url}
              onChange={(e) => set('base_url', e.target.value)}
            />
          )}
        </Field>
      ) : (
        <div className="field">
          <span className="field-label">Endpoint</span>
          {f.customUrl ? (
            <input
              aria-label="Custom endpoint"
              className={`input mono${errors.base_url ? ' invalid' : ''}`}
              placeholder={info.defaultBaseUrl}
              value={f.base_url}
              onChange={(e) => set('base_url', e.target.value)}
            />
          ) : (
            <input className="input mono" value={info.defaultBaseUrl} disabled aria-label="Endpoint" />
          )}
          {errors.base_url && <span className="error">{errors.base_url}</span>}
          <span className="hint">
            <button
              type="button"
              className="linklike"
              onClick={() => {
                set('customUrl', !f.customUrl);
                if (f.customUrl) set('base_url', '');
              }}
            >
              {f.customUrl
                ? `Use ${info.label}’s own endpoint`
                : 'Use a different endpoint (gateway or proxy)'}
            </button>
          </span>
        </div>
      )}

      <div className="field">
        <label htmlFor={ids.key}>API key{info.keyRequired ? '' : ' (optional)'}</label>
        <div className="input-row">
          <div className="input-wrap">
            <input
              id={ids.key}
              className={`input mono${errors.api_key ? ' invalid' : ''}`}
              type={showKey ? 'text' : 'password'}
              autoComplete="off"
              spellCheck={false}
              placeholder={
                editing && provider!.has_key && !f.removeKey
                  ? `Saved key ${provider!.key_hint}. Leave blank to keep it.`
                  : info.keyPlaceholder
              }
              value={f.api_key}
              disabled={f.removeKey}
              onChange={(e) => set('api_key', e.target.value)}
            />
            <button
              type="button"
              className="btn btn-ghost btn-icon reveal"
              aria-label={showKey ? 'Hide key' : 'Show key'}
              onClick={() => setShowKey((s) => !s)}
            >
              {showKey ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
        </div>
        {errors.api_key && <span className="error">{errors.api_key}</span>}
        <span className="hint">
          Stored encrypted and never shown again.
          {editing && provider!.has_key && !info.keyRequired && (
            <>
              {' '}
              <button type="button" className="linklike" onClick={() => set('removeKey', !f.removeKey)}>
                {f.removeKey ? 'Keep the saved key' : 'Remove the saved key'}
              </button>
            </>
          )}
        </span>
      </div>

      <div className="field">
        <label htmlFor={ids.models}>Model</label>
        <div className="input-row">
          <input
            id={ids.models}
            className={`input mono${errors.model ? ' invalid' : ''}`}
            list={`${ids.models}-list`}
            placeholder={info.modelPlaceholder}
            value={f.model}
            onChange={(e) => set('model', e.target.value)}
          />
          <button type="button" className="btn" onClick={loadModels} disabled={loadingModels}>
            {loadingModels ? <Loader2 size={15} className="spin" /> : <ListRestart size={15} />}
            Load models
          </button>
        </div>
        <datalist id={`${ids.models}-list`}>
          {modelOptions.map((m) => (
            <option key={m} value={m} />
          ))}
        </datalist>
        {errors.model && <span className="error">{errors.model}</span>}
        {models && (
          <span className={'error' in models || !models.ok ? 'error' : 'hint'}>
            {'error' in models
              ? models.error
              : models.ok
                ? `${models.models.length} models available. Start typing to pick one.`
                : models.message}
          </span>
        )}
        {!models && info.docsUrl && (
          <span className="hint">
            Load models lists what this key can use, or see{' '}
            <a href={info.docsUrl} target="_blank" rel="noreferrer">
              {info.label}’s model list
            </a>
            .
          </span>
        )}
      </div>

      <div className="field">
        <span className="field-label">Hosted</span>
        <div className="input-row">
          {(['external', 'self_hosted'] as const).map((h) => (
            <label key={h} className="toggle-row" style={{ flex: 1 }}>
              <input type="radio" name="hosted" checked={f.hosted === h} onChange={() => set('hosted', h)} />
              <span>
                {h === 'external' ? 'External' : 'Self-hosted'}
                <div className="hint">
                  {h === 'external'
                    ? 'Prompts are processed by the vendor, possibly outside Malaysia.'
                    : 'Runs on infrastructure we control.'}
                </div>
              </span>
            </label>
          ))}
        </div>
      </div>

      <details className="adv">
        <summary>Advanced: thinking, structured output, prices</summary>
        {info.thinkingSwitch && (
          <Field
            label="Thinking"
            hint="Thinking makes replies slower and dearer. The chat works best with it off."
          >
            {(id) => (
              <select
                id={id}
                className="input"
                value={f.thinking}
                onChange={(e) => set('thinking', e.target.value as Thinking)}
              >
                <option value="off">Off</option>
                <option value="on">On</option>
                <option value="default">Model default</option>
              </select>
            )}
          </Field>
        )}
        <Field
          label="Structured output"
          hint="How the model is asked for structured answers. Switch if the connection test says structured output failed."
        >
          {(id) => (
            <select
              id={id}
              className="input"
              value={f.structured_output}
              onChange={(e) => set('structured_output', e.target.value as StructuredOutput)}
            >
              <option value="function_calling">Function calling</option>
              <option value="json_schema">JSON schema</option>
            </select>
          )}
        </Field>
        <div className="two">
          <Field label="Input price, USD per 1M tokens" error={errors.price_in}>
            {(id) => (
              <input
                id={id}
                className="input"
                type="number"
                min={0}
                step="0.01"
                placeholder="0.00"
                value={f.price_in}
                onChange={(e) => set('price_in', e.target.value)}
              />
            )}
          </Field>
          <Field label="Output price, USD per 1M tokens" error={errors.price_out}>
            {(id) => (
              <input
                id={id}
                className="input"
                type="number"
                min={0}
                step="0.01"
                placeholder="0.00"
                value={f.price_out}
                onChange={(e) => set('price_out', e.target.value)}
              />
            )}
          </Field>
        </div>
        <p className="hint" style={{ margin: '8px 0 0', color: 'var(--muted)', fontSize: 12.5 }}>
          Prices turn token counts into spend for each tenant’s monthly AI budget. Without them, calls count
          as free.
        </p>
      </details>

      <div className="field">
        <label className="toggle-row">
          <input type="checkbox" checked={f.enabled} onChange={(e) => set('enabled', e.target.checked)} />
          <span>
            Enabled
            <div className="hint">Disabled providers are kept but never used.</div>
          </span>
        </label>
        <label className="toggle-row">
          <input
            type="checkbox"
            checked={f.is_default}
            onChange={(e) => set('is_default', e.target.checked)}
          />
          <span>
            Use as the default
            <div className="hint">The assessment chat for every tenant uses the default provider.</div>
          </span>
        </label>
        {errors.enabled && <span className="error">{errors.enabled}</span>}
      </div>

      {check && (
        <div
          className={`result ${'error' in check ? 'bad' : check.ok ? 'ok' : 'bad'}`}
          role="status"
          aria-live="polite"
        >
          {'error' in check || !check.ok ? <CircleAlert size={16} /> : <CheckCircle2 size={16} />}
          <span>{'error' in check ? check.error : check.message}</span>
        </div>
      )}
      {checking && (
        <div className="result info" role="status">
          <Loader2 size={16} className="spin" />
          <span>Calling {f.model || 'the model'}…</span>
        </div>
      )}
      <div ref={resultRef} />
    </Drawer>
  );
}

function Field({
  label,
  hint,
  error,
  children,
}: {
  label: string;
  hint?: string;
  error?: string;
  children: (id: string) => ReactNode;
}) {
  const id = useId();
  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      {children(id)}
      {error ? <span className="error">{error}</span> : hint && <span className="hint">{hint}</span>}
    </div>
  );
}

/** A saved provider in the same shape as the settings the form sends, to find what changed. */
function current(p: ProviderView) {
  return {
    name: p.name,
    base_url: p.base_url,
    model: p.model,
    hosted: p.hosted,
    reasoning: p.reasoning,
    structured_output: p.structured_output,
    price_input_per_mtok: p.price_input_per_mtok,
    price_output_per_mtok: p.price_output_per_mtok,
    enabled: p.enabled,
    is_default: p.is_default,
  };
}

const mapPath = (p: string) =>
  p === 'price_input_per_mtok' ? 'price_in' : p === 'price_output_per_mtok' ? 'price_out' : p;
